import type { FC } from 'react';

import Pill from '@/common-components/Pill';
import type { UseSignupLeadsTabResult } from '@/features/users/hooks/useSignupLeadsTab.hook';
import { timeAgo } from '@/utils/date.util';

interface SignupLeadsTableProps {
  leads: UseSignupLeadsTabResult;
}

const checkboxClass =
  'h-4 w-4 cursor-pointer rounded border-[var(--color-divider)] accent-[var(--color-brand-500)]';

/**
 * The incomplete-onboarding queue.
 *
 * These rows are signup leads, not users — they have an email and a campus and nothing else,
 * because no account was ever created. The only actions are nudging them and dismissing them.
 */
const SignupLeadsTable: FC<SignupLeadsTableProps> = ({ leads }) => {
  const allSelected = leads.leads.length > 0 && leads.selectedIds.length === leads.leads.length;

  return (
    <div>
      {leads.nudgeResult ? (
        <div className="border-b border-[var(--color-divider)] bg-[var(--color-success-bg)] px-5 py-2.5 text-[12.5px] font-semibold text-[var(--color-success-ink)]">
          Sent {leads.nudgeResult.sent}
          {leads.nudgeResult.skipped > 0 ? ` · skipped ${leads.nudgeResult.skipped}` : ''}
          {leads.nudgeResult.failed > 0 ? ` · failed ${leads.nudgeResult.failed}` : ''}
          {leads.nudgeResult.sent === 0 && leads.nudgeResult.skipped > 0
            ? ' (already nudged in the last 24h, unsubscribed, or email is not configured)'
            : ''}
        </div>
      ) : null}
      {leads.nudgeError ? (
        <div className="border-b border-[var(--color-divider)] bg-[var(--color-danger-bg)] px-5 py-2.5 text-[12.5px] font-semibold text-[var(--color-danger-ink)]">
          {leads.nudgeError}
        </div>
      ) : null}

      {leads.selectedIds.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--color-divider)] bg-[var(--color-brand-50)] px-5 py-2.5">
          <span className="text-[12.5px] font-semibold text-[var(--color-ink)]">
            {leads.selectedIds.length} selected
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={leads.onClearSelection}
              className="rounded-lg px-3 py-1.5 text-[12px] font-semibold text-[var(--color-ink-muted)] hover:bg-white"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={leads.onNudgeSelected}
              disabled={leads.isNudging}
              className="rounded-lg bg-brand-500 px-3.5 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-brand-600 disabled:opacity-60"
            >
              {leads.isNudging ? 'Sending…' : `Send nudge to ${leads.selectedIds.length}`}
            </button>
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[680px] border-collapse">
          <thead>
            <tr className="border-b border-[var(--color-divider)] bg-[var(--color-surface-muted)]">
              <th className="w-10 px-4 py-2.5 text-left">
                <input
                  type="checkbox"
                  className={checkboxClass}
                  checked={allSelected}
                  onChange={leads.onToggleSelectAll}
                  aria-label="Select all on this page"
                />
              </th>
              {['Email', 'Campus', 'Verified', 'Nudges', ''].map((heading) => (
                <th
                  key={heading}
                  className="px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.4px] text-[var(--color-ink-muted)]"
                >
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leads.leads.map((lead) => (
              <tr
                key={lead.id}
                className="border-b border-[var(--color-divider)] last:border-b-0 hover:bg-[var(--color-surface-muted)]"
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    className={checkboxClass}
                    checked={leads.selectedIds.includes(lead.id)}
                    onChange={() => leads.onToggleSelect(lead.id)}
                    aria-label={`Select ${lead.email}`}
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="text-[13px] font-semibold text-[var(--color-ink)]">
                    {lead.email}
                  </div>
                  {lead.emailOptOut ? (
                    <Pill tone="neutral" className="mt-1">
                      Unsubscribed
                    </Pill>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-[12.5px] text-[var(--color-ink-muted)]">
                  {lead.collegeName}
                </td>
                <td className="px-4 py-3 text-[12.5px] text-[var(--color-ink-muted)]">
                  {timeAgo(lead.verifiedAt)}
                </td>
                <td className="px-4 py-3 text-[12.5px] text-[var(--color-ink-muted)]">
                  {lead.nudgeCount === 0 ? (
                    '—'
                  ) : (
                    <span title={lead.lastNudgeAt ? `Last: ${timeAgo(lead.lastNudgeAt)}` : undefined}>
                      nudged {lead.nudgeCount}× ✓
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1.5">
                    <button
                      type="button"
                      onClick={() => leads.onNudgeOne(lead.id)}
                      disabled={leads.isNudging || lead.emailOptOut}
                      title={lead.emailOptOut ? 'This person unsubscribed' : undefined}
                      className="rounded-lg bg-[var(--color-brand-50)] px-3 py-1.5 text-[12px] font-semibold text-brand-600 transition-colors hover:bg-brand-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[var(--color-brand-50)] disabled:hover:text-brand-600"
                    >
                      Nudge
                    </button>
                    <button
                      type="button"
                      onClick={() => leads.onDismiss(lead.id)}
                      disabled={leads.dismissingId === lead.id}
                      className="rounded-lg px-3 py-1.5 text-[12px] font-semibold text-[var(--color-ink-muted)] transition-colors hover:bg-[var(--color-surface-muted-2)] disabled:opacity-50"
                    >
                      {leads.dismissingId === lead.id ? '…' : 'Dismiss'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SignupLeadsTable;
